import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import { mediaSrc, syllabusSrc } from '../api/media';
import { Sticker } from '../components/Cartoons';
import useAuthStore from '../store/authStore';
import useKidsStore from '../store/kidsStore';
import SubjectQuiz from './SubjectQuiz';
import InfoBanner from '../components/InfoBanner';

interface SubjectResponse {
  id: number;
  catalogId: number;
  name: string;
  displayOrder: number;
  hasSyllabus: boolean;
  syllabusFilename: string | null;
  syllabusUrl: string | null;
  syllabusStatus: 'UPLOADED' | 'PROCESSING' | 'READY' | null;
  syllabusUploadedAt: string | null;
  topicCount: number;
}

interface TopicResponse {
  topicId: number;
  topicName: string;
  explanation: string | null;
}

interface SubjectResultItem {
  id: number;
  grade: number;
  score: number;
  totalQuestions: number;
  percentage: number;
  timeTaken: number;
  attemptedAt: string;
  answersJson: string;
  subjectId: number | null;
  topicName: string;
}

interface AnswerDetail {
  questionText: string;
  optionA: string; optionB: string; optionC: string; optionD: string;
  correctOption: string;
  selectedOption: string;
  correct: boolean;
  explanation: string;
}

interface HubItem { label: string; seg: string; emoji: string; from: string; to2: string; }

// The built-in "Others" tab — the existing learning features, reused unchanged.
const HUB_ITEMS: HubItem[] = [
  { label: 'Daily Summary', seg: 'summary', emoji: '📝', from: '#FDE68A', to2: '#FCA5A5' },
  { label: 'Homework', seg: 'homework', emoji: '📚', from: '#BFDBFE', to2: '#A5B4FC' },
  { label: 'Timetable', seg: 'timetable', emoji: '🗓️', from: '#A7F3D0', to2: '#6EE7B7' },
  { label: 'Schedule', seg: 'schedule', emoji: '📅', from: '#FBCFE8', to2: '#F9A8D4' },
  { label: 'Math Quiz', seg: 'math', emoji: '🧮', from: '#DDD6FE', to2: '#C4B5FD' },
  { label: 'Reading', seg: 'reading', emoji: '📖', from: '#FED7AA', to2: '#FDBA74' },
  { label: 'Results', seg: 'history', emoji: '📊', from: '#BAE6FD', to2: '#7DD3FC' },
];

const MAX_SUBJECTS = 6;
const SYLLABUS_ACCEPT =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const SubjectsHub: React.FC<{ mode: 'parent' | 'kid' }> = ({ mode }) => {
  const navigate = useNavigate();
  const params = useParams<{ kidId: string }>();
  const { userId } = useAuthStore();
  const { kids, fetchKids } = useKidsStore();

  const canEdit = mode === 'parent';
  const kidId = mode === 'parent' ? params.kidId : userId != null ? String(userId) : undefined;

  const [subjects, setSubjects] = useState<SubjectResponse[]>([]);
  const [active, setActive] = useState<'others' | number>('others');
  const [loading, setLoading] = useState(true);

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const [topics, setTopics] = useState<TopicResponse[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [quizTopic, setQuizTopic] = useState<TopicResponse | null>(null);
  const [openTopic, setOpenTopic] = useState<number | null>(null);
  const [results, setResults] = useState<SubjectResultItem[]>([]);
  const [openResult, setOpenResult] = useState<number | null>(null);

  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === 'parent' && kids.length === 0) fetchKids();
  }, [mode, kids.length, fetchKids]);
  const kid = kids.find((k) => String(k.id) === String(kidId));

  const fetchSubjects = useCallback(async () => {
    if (!kidId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/kids/${kidId}/subjects`);
      setSubjects(res.data);
    } catch {
      /* leave existing list */
    } finally {
      setLoading(false);
    }
  }, [kidId]);

  const fetchResults = useCallback(async () => {
    if (!kidId) return;
    try {
      const res = await api.get(`/api/kids/${kidId}/subjects/results`);
      setResults(res.data);
    } catch {
      /* ignore */
    }
  }, [kidId]);

  useEffect(() => {
    fetchSubjects();
    fetchResults();
  }, [fetchSubjects, fetchResults]);

  const activeSubject = typeof active === 'number' ? subjects.find((s) => s.id === active) : undefined;

  useEffect(() => {
    setQuizTopic(null); // close any open quiz when switching tabs
    setOpenTopic(null);
    if (typeof active !== 'number' || !kidId) {
      setTopics([]);
      return;
    }
    setTopicsLoading(true);
    api
      .get(`/api/kids/${kidId}/subjects/${active}/topics`)
      .then((r) => setTopics(r.data))
      .catch(() => setTopics([]))
      .finally(() => setTopicsLoading(false));
  }, [active, kidId, subjects]);

  const addSubject = async () => {
    const name = newName.trim();
    if (!name) return;
    setError('');
    try {
      const res = await api.post(`/api/kids/${kidId}/subjects`, { name });
      setNewName('');
      setAdding(false);
      await fetchSubjects();
      setActive(res.data.id);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not add subject');
    }
  };

  const deleteSubject = async (id: number) => {
    if (!window.confirm('Remove this subject and its syllabus?')) return;
    try {
      await api.delete(`/api/kids/${kidId}/subjects/${id}`);
      if (active === id) setActive('others');
      await fetchSubjects();
    } catch {
      alert('Could not remove subject');
    }
  };

  const uploadSyllabus = async (subjectId: number, file: File) => {
    setUploadingId(subjectId);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/api/kids/${kidId}/subjects/${subjectId}/syllabus`, fd);
      await fetchSubjects();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingId(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const goto = (seg: string) =>
    navigate(mode === 'parent' ? `/parent/kids/${kidId}/${seg}` : `/kid/${seg}`);

  const tabClass = (selected: boolean) =>
    `shrink-0 px-4 py-2 rounded-full text-sm font-bold transition ${
      selected ? 'bg-pink-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50'
    }`;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="kt-card p-4 flex items-center gap-3">
        {mode === 'parent' && (
          <button onClick={() => navigate('/parent/learn')} className="text-brand-blue font-bold text-sm shrink-0">
            ←
          </button>
        )}
        {mode === 'parent' && kid?.profilePhotoUrl ? (
          <img
            src={mediaSrc(kid.profilePhotoUrl)}
            alt={kid.name}
            className="w-11 h-11 rounded-full object-cover ring-2 ring-brand-orange/30"
          />
        ) : (
          <div className="w-11 h-11 rounded-full bg-orange-100 flex items-center justify-center text-xl">🦶</div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-extrabold text-gray-800 truncate">
            {mode === 'parent' ? kid?.name || 'Kidstrack' : 'Kidstrack'} 🚀
          </h1>
          <p className="text-xs text-gray-500">
            {mode === 'parent' && kid ? `Grade ${kid.grade} · Subjects` : 'Your subjects'}
          </p>
        </div>
      </div>

      {/* Parent setup guidance */}
      {canEdit && (
        <InfoBanner
          tone="pink"
          icon="📚"
          title="Set up this child's subjects"
          steps={[
            'Tap “+ Add Subject” to add up to 6 subjects (English, Science, Social…).',
            'Open a subject and upload its syllabus (PDF, DOC or DOCX, up to 10 MB).',
            'Topics & quizzes are generated automatically — check back in a few hours.',
          ]}
          dismissKey={subjects.length > 0 ? `subjects-tip-${kidId}` : undefined}
        />
      )}

      {/* Tab strip */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        <button className={tabClass(active === 'others')} onClick={() => setActive('others')}>
          Others
        </button>
        {subjects.map((s) => (
          <button key={s.id} className={tabClass(active === s.id)} onClick={() => setActive(s.id)}>
            {s.name}
          </button>
        ))}
        {canEdit && subjects.length < MAX_SUBJECTS && (
          <button
            className="shrink-0 px-4 py-2 rounded-full text-sm font-bold text-brand-blue border border-dashed border-brand-blue/50 hover:bg-blue-50 transition"
            onClick={() => {
              setAdding(true);
              setError('');
            }}
          >
            + Add Subject
          </button>
        )}
      </div>

      {/* Add-subject inline form */}
      {adding && canEdit && (
        <div className="kt-card p-4 space-y-2">
          <p className="text-sm font-semibold text-gray-700">Add a subject</p>
          <div className="flex gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addSubject()}
              maxLength={40}
              placeholder="e.g. English, Science, Social…"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
            />
            <button onClick={addSubject} className="bg-pink-500 text-white text-sm font-bold px-4 py-2 rounded-xl">
              Add
            </button>
            <button
              onClick={() => {
                setAdding(false);
                setNewName('');
              }}
              className="text-gray-500 text-sm px-2"
            >
              Cancel
            </button>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <p className="text-xs text-gray-400">You can add up to {MAX_SUBJECTS} subjects.</p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : active === 'others' ? (
        <div className="grid grid-cols-2 gap-3">
          {HUB_ITEMS.map((it) => (
            <button
              key={it.seg}
              onClick={() => goto(it.seg)}
              className="kt-card p-4 flex flex-col items-start gap-2 hover:shadow-md transition active:scale-[0.98] text-left"
            >
              <Sticker emoji={it.emoji} from={it.from} to={it.to2} size={48} />
              <p className="font-display font-bold text-gray-800 leading-tight">{it.label}</p>
            </button>
          ))}
          {mode === 'parent' && (
            <button
              onClick={() => goto('activity')}
              className="kt-card p-4 flex flex-col items-start gap-2 hover:shadow-md transition active:scale-[0.98] text-left"
            >
              <Sticker emoji="⚙️" from="#E5E7EB" to="#CBD5E1" size={48} />
              <p className="font-display font-bold text-gray-800 leading-tight">Overview &amp; Settings</p>
            </button>
          )}
        </div>
      ) : activeSubject ? (
        <div className="space-y-4">
          {/* Syllabus */}
          <section className="kt-card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-gray-800">{activeSubject.name} Syllabus</h2>
              {canEdit && (
                <button
                  onClick={() => deleteSubject(activeSubject.id)}
                  className="text-xs text-red-400 hover:text-red-600"
                >
                  Remove subject
                </button>
              )}
            </div>

            {activeSubject.hasSyllabus ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2">
                  <span className="text-xl">📄</span>
                  <div className="flex-1 min-w-0">
                    <a
                      href={syllabusSrc(activeSubject.syllabusUrl!)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-brand-blue hover:underline truncate block"
                    >
                      {activeSubject.syllabusFilename || 'View syllabus'}
                    </a>
                    <p className="text-[11px] text-gray-400">
                      {activeSubject.syllabusStatus === 'READY'
                        ? 'Ready'
                        : activeSubject.syllabusStatus === 'PROCESSING'
                        ? 'Preparing topics…'
                        : 'Uploaded'}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <label
                    htmlFor="syllabus-file"
                    className="text-xs font-semibold text-brand-blue cursor-pointer hover:underline"
                  >
                    {uploadingId === activeSubject.id ? 'Uploading…' : 'Replace syllabus'}
                  </label>
                )}
              </div>
            ) : canEdit ? (
              <label
                htmlFor="syllabus-file"
                className="flex flex-col items-center justify-center gap-1 w-full border-2 border-dashed border-pink-300 bg-pink-50 hover:bg-pink-100 rounded-xl py-8 px-4 cursor-pointer transition text-center"
              >
                <span className="text-3xl">☁️⬆️</span>
                <span className="text-sm font-bold text-pink-600 mt-1">
                  {uploadingId === activeSubject.id ? 'Uploading…' : 'Upload Syllabus Document'}
                </span>
                <span className="text-xs text-gray-400">PDF, DOC, DOCX (Max 10MB)</span>
              </label>
            ) : (
              <p className="text-sm text-gray-400">No syllabus uploaded yet.</p>
            )}

            {canEdit && (
              <input
                id="syllabus-file"
                ref={fileInput}
                type="file"
                accept={SYLLABUS_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadSyllabus(activeSubject.id, f);
                }}
              />
            )}
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </section>

          {/* Active quiz takes over the topics area */}
          {quizTopic ? (
            <SubjectQuiz
              kidId={kidId!}
              subjectId={activeSubject.id}
              topic={quizTopic}
              onClose={() => { setQuizTopic(null); fetchResults(); }}
            />
          ) : (
            <section className="kt-card p-5 space-y-3">
              <h2 className="font-display font-bold text-gray-800">Topics &amp; Quizzes</h2>
              {topicsLoading ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : topics.length === 0 ? (
                activeSubject.hasSyllabus && activeSubject.syllabusStatus !== 'READY' ? (
                  <InfoBanner tone="amber" icon="⏳" title="Syllabus uploaded — generating content">
                    Topics &amp; quizzes are being generated automatically from the syllabus.
                    Please check back in a few hours.
                  </InfoBanner>
                ) : (
                  <p className="text-sm text-gray-400">
                    {canEdit
                      ? 'Upload a syllabus above and the topics will appear here.'
                      : 'Your topics are being prepared — check back soon.'}
                  </p>
                )
              ) : (
                <ul className="space-y-2">
                  {topics.map((t) => (
                    <li key={t.topicId} className="bg-gray-50 rounded-xl px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <button
                          onClick={() => setOpenTopic(openTopic === t.topicId ? null : t.topicId)}
                          className="flex-1 text-left"
                        >
                          <p className="text-sm font-semibold text-gray-800">{t.topicName}</p>
                          {t.explanation && (
                            <p className="text-[11px] text-brand-blue mt-0.5">
                              {openTopic === t.topicId ? 'Hide study guide ▲' : 'Study guide ▼'}
                            </p>
                          )}
                        </button>
                        <button
                          onClick={() => setQuizTopic(t)}
                          className="shrink-0 bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition"
                        >
                          Take quiz
                        </button>
                      </div>
                      {openTopic === t.topicId && t.explanation && (
                        <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap leading-relaxed">
                          {t.explanation}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {/* Results for this subject */}
          {!quizTopic && (() => {
            const subjResults = results.filter((r) => r.subjectId === activeSubject.id);
            const avg = subjResults.length
              ? Math.round(subjResults.reduce((s, r) => s + (r.percentage || 0), 0) / subjResults.length)
              : null;
            const parse = (j: string): AnswerDetail[] => { try { return JSON.parse(j) || []; } catch { return []; } };
            const border = (p: number) => (p >= 80 ? 'border-green-400' : p >= 60 ? 'border-yellow-400' : 'border-red-400');
            const color = (p: number) => (p >= 80 ? 'text-green-600' : p >= 60 ? 'text-yellow-500' : 'text-red-400');
            return (
              <section className="kt-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-display font-bold text-gray-800">Results</h2>
                  {avg !== null && (
                    <span className="text-xs font-semibold text-pink-500">{subjResults.length} attempts · Avg {avg}%</span>
                  )}
                </div>
                {subjResults.length === 0 ? (
                  <p className="text-sm text-gray-400">No quiz attempts yet — take a quiz on a topic above.</p>
                ) : (
                  <div className="space-y-2">
                    {subjResults.map((r) => (
                      <div key={r.id} className={`bg-gray-50 rounded-xl border-l-4 ${border(r.percentage)}`}>
                        <button
                          onClick={() => setOpenResult(openResult === r.id ? null : r.id)}
                          className="w-full text-left p-3 flex justify-between items-start"
                        >
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{r.topicName || 'Quiz'}</p>
                            <p className="text-[11px] text-gray-400">
                              {new Date(r.attemptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {' · '}{openResult === r.id ? 'Hide review ▲' : 'Review ▼'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${color(r.percentage)}`}>{Math.round(r.percentage)}%</p>
                            <p className="text-[11px] text-gray-400">{r.score}/{r.totalQuestions}</p>
                          </div>
                        </button>
                        {openResult === r.id && (
                          <div className="border-t border-gray-100 p-3 space-y-2">
                            {parse(r.answersJson).map((a, i) => (
                              <div key={i} className={`rounded-lg p-2 text-xs ${a.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                                <p className="font-medium text-gray-800 mb-1">{i + 1}. {a.questionText} {a.correct ? '✓' : '✗'}</p>
                                {['A', 'B', 'C', 'D'].map((opt) => (
                                  <div key={opt} className={`px-2 py-0.5 rounded mb-0.5
                                    ${opt === a.correctOption ? 'bg-green-200 text-green-800 font-semibold'
                                      : opt === a.selectedOption && !a.correct ? 'bg-red-200 text-red-700' : 'text-gray-600'}`}>
                                    {opt}. {(a as any)[`option${opt}`]}
                                  </div>
                                ))}
                                {a.explanation && <p className="text-gray-400 mt-1 italic">💡 {a.explanation}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })()}
        </div>
      ) : null}
    </div>
  );
};

export default SubjectsHub;
